<template>
  <div class="tte">
    <div class="tte-head">
      <div v-if="scope === 'operator'">
        <h3>Platform default email templates</h3>
        <p class="tte-sub">These are the platform's default templates. Every workspace that has not made its own copy
          of a template uses the one published here, so a change you publish reaches all of them.</p>
      </div>
      <div v-else>
        <h3>Email templates</h3>
        <p class="tte-sub">Edit the block layout on the left; the preview on the right is the exact render your
          audience receives.</p>
      </div>
      <div class="tte-actions">
        <button class="btn sm" :disabled="saving" @click="save()">Save draft</button>
        <button class="btn sm primary" :disabled="saving" @click="publish()">Publish</button>
      </div>
    </div>

    <div v-if="error" class="tte-alert">{{ error }}</div>

    <!-- template library -->
    <div class="tpl-lib">
      <button v-for="t in templatesForSurface" :key="t.slug" class="tpl-pill" :class="{ active: t.slug === sel.slug }"
              @click="openTemplate(t.slug)">
        <span class="tname">{{ t.title || t.slug }}</span>
        <span class="chip" :class="chipClass(t)">{{ chipLabel(t) }}</span>
      </button>
      <template v-if="creating">
        <span class="tpl-pill creating">
          <input v-model="newName" class="new-input" placeholder="Template name" @keyup.enter="createTemplate" />
          <button class="btn xs primary" @click="createTemplate">Create</button>
          <button class="btn xs ghost" @click="creating = false">✕</button>
        </span>
      </template>
      <button v-else class="tpl-pill new" @click="creating = true">+ New template</button>
    </div>

    <!-- split editor / preview -->
    <div class="split">
      <!-- editor -->
      <div class="editor-col">
        <div class="col-bar">
          <CygnusIcon name="pen" /> Editing · <span class="strong">{{ sel.title || sel.slug }}</span>
          <span class="spacer"></span>
          <button v-if="tokenPickerComp" class="btn xs ghost token-btn" @click="showTokens = !showTokens">
            <CygnusIcon name="bolt" /> Insert token
          </button>
        </div>

        <component v-if="tokenPickerComp && showTokens" :is="tokenPickerComp" label="Merge tokens" dense
                   class="token-picker" @insert="onInsertToken" />

        <div class="blocks" @focusin="onBlocksFocusIn">
          <draggable v-model="sel.blocks" item-key="_key" handle=".grip" :animation="150" ghost-class="block-ghost">
            <template #item="{ element: b, index: i }">
              <div class="block">
                <div class="bh">
                  <span class="grip" title="Drag to reorder"><CygnusIcon name="more" /></span>
                  <select v-if="isEditable(b)" class="bsel" v-model="b.type">
                    <option v-for="bt in blockTypes" :key="bt" :value="bt">{{ bt }}</option>
                  </select>
                  <!-- A block type this editor has no fields for: shown, not
                       editable, and saved back exactly as it was loaded. -->
                  <span v-else class="btype-ro">{{ b.type || 'unknown block' }}</span>
                  <span class="bmove">
                    <button class="ib" :disabled="i === 0" title="Move up" aria-label="Move up" @click="move(i, -1)"><CygnusIcon name="chevron-up" /></button>
                    <button class="ib" :disabled="i === sel.blocks.length - 1" title="Move down" aria-label="Move down" @click="move(i, 1)"><CygnusIcon name="chevron-down" /></button>
                    <button class="ib danger" title="Delete" aria-label="Delete" @click="removeBlock(i)"><CygnusIcon name="trash" /></button>
                  </span>
                </div>
                <input v-if="b.type === 'heading'" class="binput" v-model="b.text" placeholder="Heading text (supports {brand.app_name})"
                       :data-block-index="i" data-block-field="text" />
                <textarea v-else-if="b.type === 'markdown'" class="binput" rows="2" v-model="b.body" placeholder="Body text — {{contact.first_name}} personalises"
                          :data-block-index="i" data-block-field="body"></textarea>
                <input v-else-if="b.type === 'image'" class="binput" v-model="b.src" placeholder="Image URL"
                       :data-block-index="i" data-block-field="src" />
                <div v-else-if="b.type === 'divider'" class="bnote">Horizontal divider</div>
                <div v-else-if="b.type === 'content'" class="bnote">Dynamic content slot — the campaign body is injected here.</div>
                <div v-else-if="!isEditable(b)" class="bnote">{{ readOnlyNote }}</div>
              </div>
            </template>
          </draggable>
          <div v-if="!sel.blocks.length" class="blocks-empty">No blocks yet — add one below.</div>
        </div>

        <div class="addblocks">
          <button v-for="bt in addable" :key="bt" class="add-b" @click="addBlock(bt)"><CygnusIcon name="plus" /> {{ bt }}</button>
        </div>
      </div>

      <!-- preview -->
      <div class="preview-col">
        <div class="pv-bar">
          <div class="seg">
            <button :class="{ on: mode === 'desktop' }" @click="mode = 'desktop'"><CygnusIcon name="window" /> Desktop</button>
            <button :class="{ on: mode === 'mobile' }" @click="mode = 'mobile'"><CygnusIcon name="mobile" /> Mobile</button>
          </div>
          <span class="pv-live"><span class="lp"></span> {{ preview.loading ? 'Rendering…' : 'Live' }}</span>
        </div>
        <div v-if="preview.error" class="pv-warn">{{ preview.error }}</div>
        <div class="pv-stage">
          <iframe class="pv-doc" :class="mode" sandbox="" :srcdoc="preview.html"></iframe>
        </div>
      </div>
    </div>

    <div class="send-test">
      <button class="btn sm" :disabled="testing" @click="sendTest()"><CygnusIcon name="envelope" /> Send test to myself</button>
      <span class="st-copy">Delivers this exact render to <b>{{ myEmail || 'your account email' }}</b> — merge tokens use sample data.
        <span v-if="testMsg" class="st-ok">{{ testMsg }}</span></span>
    </div>
  </div>
</template>

<script>
import useApi from '@apexl/icarus-core/src/services/api';
import draggable from 'vuedraggable';
import {
  EDITABLE_BLOCK_TYPES, ADDABLE_BLOCK_TYPES, isEditableBlock, toEditorBlocks, toPlainBlocks,
} from '@root/services/templateBlocks.mjs';

/**
 * TenantTemplateEditor (Musubi) — the workspace-owner email-template editor at
 * /admin/email-templates (Ops::OUTREACH). Replaces the vendor tab-hopping
 * editor for tenants with ONE canvas: block editor left (~45%), a large live
 * preview right (~55%) that repaints off the SAME server renderer the real send
 * uses (POST /branding/templates/preview, debounced), a desktop/mobile toggle,
 * and a "Send test to myself" confidence step. The BrandAdmin wrapper decides
 * which editor to mount, and passes `scope`.
 *
 * Operators get this editor too, as the default on the email templates page
 * (`scope="operator"`): the same endpoints then read and write the platform
 * default templates (tenant_id NULL), which the heading says, and the full
 * vendor editor is one switch away in the wrapper.
 *
 * A block of a type this editor has no fields for (the full editor's
 * `content_library_block`, or anything newer) is shown as a read-only row
 * labelled with its type and is saved back exactly as it was loaded: see
 * services/templateBlocks.mjs. It can still be moved or deleted.
 *
 * Every user-initiated action clears `error` before it starts (2026-08-13):
 * the catch blocks below SET the banner but nothing ever cleared it, so a
 * single failure pinned a red alert to the page for the rest of the session,
 * across every subsequent action that worked fine. The banner now only ever
 * describes the most recent action.
 */
export default {
  name: 'TenantTemplateEditor',
  components: { draggable },
  props: {
    apiBase: { type: String, default: '/api/v1' },
    surface: { type: String, default: 'email' },
    // The brand scope GET /branding/surface reported to the BrandAdmin
    // wrapper: 'tenant' (a workspace's own templates) or 'operator' (the
    // platform defaults).
    scope: { type: String, default: 'tenant' },
  },
  data() {
    return {
      api: useApi(),
      templates: [],
      sel: { surface: this.surface, slug: 'default', blocks: [], title: '' },
      blockTypes: EDITABLE_BLOCK_TYPES,
      addable: ADDABLE_BLOCK_TYPES,
      mode: 'desktop',
      preview: { html: '', loading: false, error: '' },
      previewReqId: 0, previewTimer: null,
      keySeq: 0,
      saving: false, testing: false, error: '', testMsg: '',
      myEmail: '',
      creating: false, newName: '',
      showTokens: false, lastFocusedBlock: null,
    };
  },
  computed: {
    templatesForSurface() {
      return this.templates.filter(t => t.surface === this.surface).slice().sort((a, b) => {
        if (a.slug === 'default') return -1;
        if (b.slug === 'default') return 1;
        return (b.modified || 0) - (a.modified || 0);
      });
    },
    readOnlyNote() {
      return this.scope === 'operator'
        ? 'This block type is edited in the full editor. Here it is kept exactly as it is.'
        : 'This block type cannot be edited here. It is kept exactly as it is.';
    },
    tokenPickerComp() {
      const inst = this.$root && this.$root.$;
      return (inst && inst.appContext && inst.appContext.components && inst.appContext.components.MergeTokenPicker) || null;
    },
  },
  watch: {
    'sel.blocks': { deep: true, handler() { this.schedulePreview(); } },
  },
  mounted() {
    this.loadTemplates();
    this.openTemplate('default');
    this.loadMe();
    // ?new=1 - a deep link straight into the create form, so "+ Create new
    // template" elsewhere in the app lands on the thing it promised rather than
    // on a list the operator has to re-navigate.
    if (String(this.$route?.query?.new ?? '') === '1') this.creating = true;
  },
  methods: {
    async loadMe() {
      try { const res = await this.api.get(`${this.apiBase}/shell/me`); this.myEmail = res?.user?.email || ''; } catch (e) { /* non-fatal */ }
    },
    async loadTemplates() {
      this.error = '';
      try {
        const res = await this.api.get(`${this.apiBase}/branding/templates`);
        this.templates = res?.templates || [];
      } catch (e) { this.error = 'Failed to load templates.'; }
    },
    async openTemplate(slug) {
      this.error = '';
      try {
        const res = await this.api.get(`${this.apiBase}/branding/templates/${this.surface}/${slug}`);
        const blocks = toEditorBlocks(res?.blocks, () => ++this.keySeq);
        this.sel = { surface: this.surface, slug, blocks, title: res?.title || '' };
        this.testMsg = '';
        this.schedulePreview(true);
      } catch (e) { this.error = 'Failed to open template.'; }
    },
    plainBlocks() { return toPlainBlocks(this.sel.blocks); },
    isEditable(b) { return isEditableBlock(b); },
    addBlock(type) {
      const b = { type, _key: ++this.keySeq };
      if (type === 'heading') b.text = '';
      else if (type === 'markdown') b.body = '';
      else if (type === 'image') b.src = '';
      this.sel.blocks.push(b);
    },
    removeBlock(i) { this.sel.blocks.splice(i, 1); },
    move(i, dir) {
      const j = i + dir, arr = this.sel.blocks;
      if (j < 0 || j >= arr.length) return;
      [arr[i], arr[j]] = [arr[j], arr[i]];
    },

    async save() {
      this.error = '';
      this.saving = true;
      try {
        await this.api.put(`${this.apiBase}/branding/templates/${this.surface}/${this.sel.slug}`, {
          title: this.sel.title, blocks: this.plainBlocks(),
        });
        await this.loadTemplates();
      } catch (e) { this.error = 'Failed to save template.'; } finally { this.saving = false; }
    },
    async publish() {
      this.error = '';
      this.saving = true;
      try {
        await this.api.put(`${this.apiBase}/branding/templates/${this.surface}/${this.sel.slug}`, {
          title: this.sel.title, blocks: this.plainBlocks(),
        });
        await this.api.post(`${this.apiBase}/branding/templates/${this.surface}/${this.sel.slug}/publish`);
        await this.loadTemplates();
      } catch (e) { this.error = 'Failed to publish template.'; } finally { this.saving = false; }
    },
    async createTemplate() {
      const title = (this.newName || '').trim();
      if (!title) return;
      const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'template';
      this.error = '';
      this.saving = true;
      try {
        await this.api.put(`${this.apiBase}/branding/templates/${this.surface}/${slug}`, { title, blocks: [] });
        this.creating = false; this.newName = '';
        await this.loadTemplates();
        await this.openTemplate(slug);
      } catch (e) { this.error = 'Failed to create template.'; } finally { this.saving = false; }
    },

    // Debounced live preview — same server renderer the real send uses.
    schedulePreview(immediate = false) {
      if (this.previewTimer) { clearTimeout(this.previewTimer); this.previewTimer = null; }
      if (immediate) { this.fetchPreview(); return; }
      this.previewTimer = setTimeout(() => this.fetchPreview(), 600);
    },
    async fetchPreview() {
      const reqId = ++this.previewReqId;
      this.preview.loading = true;
      try {
        const res = await this.api.post(`${this.apiBase}/branding/templates/preview`, { blocks: this.plainBlocks() });
        if (reqId !== this.previewReqId) return;
        this.preview.html = `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:16px;background:#fff;font-family:-apple-system,Segoe UI,sans-serif}</style></head><body>${res?.email || ''}</body></html>`;
        this.preview.error = '';
      } catch (e) {
        if (reqId !== this.previewReqId) return;
        this.preview.error = 'Failed to render preview.';
      } finally {
        if (reqId === this.previewReqId) this.preview.loading = false;
      }
    },

    async sendTest() {
      this.error = '';
      this.testing = true; this.testMsg = '';
      try {
        const res = await this.api.post(`${this.apiBase}/branding/test-send`, {
          blocks: this.plainBlocks(), subject: this.sel.title || 'Your email template',
        });
        this.testMsg = `Queued — check ${res?.to || 'your inbox'}.`;
      } catch (e) {
        // The server says why (sending not switched on, address suppressed,
        // or the workspace's hourly test limit reached).
        this.error = e?.response?.data?.error || 'Failed to send test.';
      } finally { this.testing = false; }
    },

    onBlocksFocusIn(ev) {
      const el = ev.target;
      if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return;
      const idx = el.dataset ? el.dataset.blockIndex : undefined;
      const field = el.dataset ? el.dataset.blockField : undefined;
      if (idx === undefined || field === undefined) return;
      this.lastFocusedBlock = { el, index: parseInt(idx, 10), field };
    },
    onInsertToken(token) {
      const fb = this.lastFocusedBlock;
      const block = fb && this.sel.blocks[fb.index];
      if (!fb || !fb.el || !block) return;
      const el = fb.el, current = block[fb.field] || '';
      const start = el.selectionStart ?? current.length, end = el.selectionEnd ?? current.length;
      block[fb.field] = current.slice(0, start) + token + current.slice(end);
      this.$nextTick(() => { if (el.isConnected) { el.focus(); const p = start + token.length; el.setSelectionRange(p, p); } });
    },

    chipLabel(t) {
      if (t.inherited) return 'Platform default';
      if (t.status === 'published') return 'Published';
      return 'Draft';
    },
    chipClass(t) {
      if (t.inherited) return 'def';
      if (t.status === 'published') return 'pub';
      return 'draft';
    },
  },
};
</script>

<style scoped lang="scss">
.tte {
  --line: var(--m-border, #e4e7ec);
  --line-2: var(--m-border, #eef0f3);
  --surface: var(--m-bg-card, #fff);
  --surface-2: #fbfbfd;
  --ink: var(--m-text-primary, #16181d);
  --ink-2: var(--m-text-secondary, #4a4f5c);
  --ink-3: var(--m-text-tertiary, #787f8c);
  --indigo: var(--m-accent, #4f46e5);
  --indigo-wash: var(--m-accent-soft, #eef0fe);
  --teal: var(--m-accent-2, #14b8a6);
  color: var(--ink);

  .tte-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
  .tte-head h3 { font-size: 19px; font-weight: 650; margin: 0 0 4px; }
  .tte-sub { color: var(--ink-2); font-size: 13.5px; margin: 0; max-width: 560px; }
  .tte-actions { display: flex; gap: 8px; flex: none; }
  .tte-alert { background: #fbeaea; color: #b23636; border-radius: 8px; padding: 10px 14px; font-size: 13px; margin-bottom: 14px; }

  .btn { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--line); background: #fff; color: var(--ink); font: inherit; font-size: 13px; font-weight: 600; padding: 7px 13px; border-radius: 9px; cursor: pointer; }
  .btn.primary { background: var(--indigo); border-color: var(--indigo); color: #fff; }
  .btn.ghost { background: transparent; }
  .btn.sm { padding: 6px 12px; font-size: 12.5px; }
  .btn.xs { padding: 3px 8px; font-size: 11.5px; border-radius: 7px; }
  .btn:disabled { opacity: .55; cursor: default; }

  .tpl-lib { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
  .tpl-pill { display: inline-flex; align-items: center; gap: 8px; border: 1px solid var(--line); border-radius: 10px; padding: 7px 12px; background: #fff; font-size: 12.5px; cursor: pointer; color: var(--ink); }
  .tpl-pill.active { border-color: var(--indigo); background: var(--indigo-wash); }
  .tpl-pill.new { border-style: dashed; color: var(--ink-3); }
  .tpl-pill.creating { cursor: default; gap: 6px; }
  .tpl-pill .tname { font-weight: 600; }
  .new-input { border: 1px solid var(--line); border-radius: 7px; padding: 4px 8px; font: inherit; font-size: 12px; }

  .chip { display: inline-flex; align-items: center; font-size: 10.5px; font-weight: 650; padding: 2px 8px; border-radius: 20px; }
  .chip.def { background: #f1f3f6; color: #667085; }
  .chip.pub { background: #e7f6ec; color: #177a3d; }
  .chip.draft { background: #f1f3f6; color: #667085; }

  .split { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr); border: 1px solid var(--line); border-radius: 14px; overflow: hidden; background: #fff; }
  .editor-col { border-right: 1px solid var(--line); min-width: 0; }
  .col-bar { display: flex; align-items: center; gap: 8px; padding: 11px 16px; border-bottom: 1px solid var(--line); background: var(--surface-2); font-size: 12px; font-weight: 600; color: var(--ink-2); }
  .col-bar .strong { color: var(--ink); }
  .col-bar .spacer { flex: 1; }
  .token-btn { color: #0e7c70; border-color: var(--m-accent-2, #14b8a6); }
  .token-picker { margin: 10px 16px 0; }

  .blocks { padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; }
  .block { border: 1px solid var(--line); border-radius: 10px; background: #fff; padding: 10px 12px; }
  .block-ghost { opacity: .5; border-color: var(--indigo); background: var(--indigo-wash); }
  .bh { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
  .grip { color: var(--ink-3); font-size: 13px; cursor: grab; touch-action: none; }
  .grip:active { cursor: grabbing; }
  .bsel { font-size: 11.5px; font-weight: 600; border: 1px solid var(--line); border-radius: 6px; padding: 3px 8px; background: var(--surface-2); color: var(--ink-2); }
  .bmove { margin-left: auto; display: flex; gap: 2px; }
  .ib { border: 1px solid var(--line); background: #fff; color: var(--ink-3); border-radius: 6px; width: 26px; height: 24px; cursor: pointer; font-size: 11px; }
  .ib.danger:hover { color: #b23636; border-color: #f0c9c9; }
  .ib:disabled { opacity: .4; cursor: default; }
  .binput { width: 100%; border: 1px solid var(--line-2); border-radius: 7px; padding: 7px 10px; font: inherit; font-size: 12.5px; background: var(--surface-2); color: var(--ink); }
  .bnote { font-size: 12px; color: var(--ink-3); font-style: italic; }
  .btype-ro { font-size: 11.5px; font-weight: 600; border: 1px dashed var(--line); border-radius: 6px; padding: 3px 8px; color: var(--ink-2); }
  .blocks-empty { font-size: 12.5px; color: var(--ink-3); padding: 20px 4px; text-align: center; }
  .addblocks { padding: 0 16px 16px; display: flex; flex-wrap: wrap; gap: 6px; }
  .add-b { font-size: 11.5px; border: 1px dashed var(--line); background: #fff; border-radius: 8px; padding: 5px 10px; color: var(--ink-2); cursor: pointer; display: inline-flex; gap: 5px; align-items: center; }

  .preview-col { min-width: 0; background: var(--surface-2); }
  .pv-bar { display: flex; align-items: center; gap: 10px; padding: 9px 16px; border-bottom: 1px solid var(--line); background: #fff; }
  .seg { display: inline-flex; border: 1px solid var(--line); border-radius: 8px; overflow: hidden; }
  .seg button { border: none; background: #fff; font: inherit; font-size: 12px; font-weight: 600; padding: 5px 12px; color: var(--ink-2); cursor: pointer; display: flex; align-items: center; gap: 6px; }
  .seg button.on { background: var(--ink); color: #fff; }
  .pv-live { margin-left: auto; font-size: 11px; color: #0e7c70; display: inline-flex; align-items: center; gap: 6px; font-weight: 600; }
  .pv-live .lp { width: 7px; height: 7px; border-radius: 50%; background: var(--teal); animation: tte-pulse 1.4s infinite; }
  @keyframes tte-pulse { 0%,100% { opacity: .3; } 50% { opacity: 1; } }
  .pv-warn { font-size: 12px; color: #8a5a00; background: #fdf3e2; padding: 8px 16px; }
  .pv-stage { padding: 22px; display: grid; place-items: start center; min-height: 440px; }
  .pv-doc { width: 100%; max-width: 560px; height: 520px; background: #fff; border: 1px solid var(--line); border-radius: 12px; box-shadow: var(--m-shadow-card, 0 1px 3px rgba(16,24,40,.08)); transition: max-width .2s; }
  .pv-doc.mobile { max-width: 320px; }

  .send-test { margin-top: 14px; display: flex; align-items: center; gap: 12px; font-size: 12.5px; color: var(--ink-2); flex-wrap: wrap; }
  .st-copy { max-width: 640px; }
  .st-ok { color: #177a3d; font-weight: 600; margin-left: 6px; }

  @media (max-width: 900px) {
    .split { grid-template-columns: 1fr; }
    .editor-col { border-right: none; border-bottom: 1px solid var(--line); }
  }
}
</style>
